import React, { forwardRef } from 'react';

const GatePassPrint = forwardRef(({ gatePass, copyType = 'ORIGINAL' }, ref) => {
  if (!gatePass) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      sales_dispatch: 'Sales Dispatch',
      purchase_return: 'Purchase Return',
      inter_unit_transfer: 'Inter-Unit Transfer',
      sample_outward: 'Sample Outward',
      supplier_sample_return: 'Supplier Sample Return',
      sales_replacement: 'Sales Replacement',
      job_work_ojc: 'Job Work (OJC)',
      repair_amc: 'Repair/AMC',
      scrap_disposal: 'Scrap Disposal',
      exhibition_demo: 'Exhibition/Demo',
      loan_rental: 'Loan/Rental',
      personal: 'Personal',
    };
    return labels[category] || category;
  };

  return (
    <div ref={ref} className="p-6 bg-white print:p-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="text-center border-b-2 border-black pb-3 mb-4">
        <h1 className="text-2xl font-bold">GATE PASS</h1>
        <p className="text-sm text-gray-600">[{copyType}]</p>
      </div>

      {/* Gate Pass Info */}
      <div className="flex justify-between mb-4 text-sm">
        <div>
          <p><strong>Gate Pass No.:</strong> {gatePass.gate_pass_number}</p>
          <p><strong>Category:</strong> {getCategoryLabel(gatePass.category)}</p>
        </div>
        <div className="text-right">
          <p><strong>Date:</strong> {formatDate(gatePass.created_at)}</p>
          <p><strong>Location:</strong> {gatePass.location_name}</p>
        </div>
      </div>

      {/* Divider */}
      <hr className="border-gray-400 mb-4" />

      {/* Party Details */}
      <div className="mb-4">
        <h3 className="font-bold text-sm uppercase mb-2">Party Details</h3>
        <p><strong>Name:</strong> {gatePass.party_name}</p>
        {gatePass.source_number && (
          <p><strong>Reference:</strong> {gatePass.source_number}</p>
        )}
      </div>

      {/* Divider */}
      <hr className="border-gray-400 mb-4" />

      {/* Items Table */}
      <div className="mb-4">
        <h3 className="font-bold text-sm uppercase mb-2">Items</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-2 text-left w-12">#</th>
              <th className="border border-gray-400 p-2 text-left">Item</th>
              <th className="border border-gray-400 p-2 text-right w-24">Quantity</th>
              <th className="border border-gray-400 p-2 text-left w-20">Unit</th>
            </tr>
          </thead>
          <tbody>
            {gatePass.items?.map((item, idx) => (
              <tr key={item.id || idx}>
                <td className="border border-gray-400 p-2">{idx + 1}</td>
                <td className="border border-gray-400 p-2">
                  {item.item_name}
                  {item.item_code && <span className="text-gray-500 text-xs ml-2">({item.item_code})</span>}
                </td>
                <td className="border border-gray-400 p-2 text-right">{item.quantity}</td>
                <td className="border border-gray-400 p-2">{item.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Divider */}
      <hr className="border-gray-400 mb-4" />

      {/* Transport Details */}
      <div className="mb-4">
        <h3 className="font-bold text-sm uppercase mb-2">Transport Details</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <p><strong>Vehicle No.:</strong> {gatePass.vehicle_number || '________________'}</p>
          <p><strong>Driver:</strong> {gatePass.driver_name || '________________'}</p>
          <p><strong>Contact:</strong> {gatePass.driver_contact || '________________'}</p>
          <p><strong>Transporter:</strong> {gatePass.transporter_name || '________________'}</p>
        </div>
      </div>

      {/* Divider */}
      <hr className="border-gray-400 mb-4" />

      {/* Document References */}
      {(gatePass.dc_number || gatePass.invoice_number || gatePass.eway_bill_number) && (
        <div className="mb-4">
          <h3 className="font-bold text-sm uppercase mb-2">Document References</h3>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {gatePass.dc_number && <p><strong>DC No.:</strong> {gatePass.dc_number}</p>}
            {gatePass.invoice_number && <p><strong>Invoice:</strong> {gatePass.invoice_number}</p>}
            {gatePass.eway_bill_number && <p><strong>E-way Bill:</strong> {gatePass.eway_bill_number}</p>}
          </div>
          <hr className="border-gray-400 my-4" />
        </div>
      )}

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-8 text-sm mb-4">
        <div>
          <p><strong>Created By:</strong> {gatePass.created_by_name}</p>
          <p><strong>Time:</strong> {formatTime(gatePass.created_at)}</p>
        </div>
        <div>
          <p><strong>Verified By:</strong> {gatePass.out_by_name || '________________'}</p>
          <p><strong>Time:</strong> {gatePass.out_at ? formatTime(gatePass.out_at) : '______'}</p>
          <p className="text-gray-500 text-xs">(Security)</p>
        </div>
      </div>

      {/* Divider */}
      <hr className="border-gray-400 mb-4" />

      {/* Return Info & Remarks */}
      <div className="text-sm">
        {gatePass.requires_return && (
          <p className="mb-2">
            <input type="checkbox" checked readOnly className="mr-2" />
            <strong>RETURNABLE</strong> - Expected Return: {gatePass.expected_return_date || '_______________'}
          </p>
        )}
        <p><strong>Remarks:</strong> {gatePass.remarks || '_______________________________________________'}</p>
      </div>
    </div>
  );
});

GatePassPrint.displayName = 'GatePassPrint';

export default GatePassPrint;
